// Add this to a custom JavaScript file in your theme
class CartValidator {
  constructor() {
    this.checkoutSelectors = [
      '.mini-cart__button-container a[href="/cart"]',
      'button[name="checkout"]',
    ];

    this.init();
  }

  init() {
    this.validateCart();
    this.setupEventListeners();
  }

  setupEventListeners() {
    document.addEventListener("cart:updated", () => this.validateCart());
    document.addEventListener("cart:refresh", () => this.validateCart());
    document.addEventListener("cart.requestComplete", () =>
      this.validateCart()
    );
    document.addEventListener("draw.cart", () => this.validateCart());
    document.addEventListener("cart.rendered", () => this.validateCart());
    this.observeCartChanges();
  }

  observeCartChanges() {
    const cartContainers = document.querySelectorAll(
      'form[action="/cart"],' +
        "[data-cart-container]," +
        "#mini-cart," +
        ".cart-drawer"
    );

    const observer = new MutationObserver(() => this.validateCart());

    cartContainers.forEach((container) => {
      if (container) {
        observer.observe(container, {
          subtree: true,
          childList: true,
          attributes: true,
        });
      }
    });
  }

  async validateCart() {
    // One run at a time; a change during a run triggers one more run after it.
    if (this.running) {
      this.pending = true;
      return;
    }
    this.running = true;
    try {
      const cart = await this.fetchCart();
      const { foodCount, packagingCount } = await this.countItemsByTag(cart);
      this.lastCounts = { foodCount, packagingCount };   // read by the delivery-day rules
      const isValid = this.isCartValid(foodCount, packagingCount);
      this.updateCheckoutState(isValid);
    } catch (error) {
      console.error("Cart validation error:", error);
    } finally {
      this.running = false;
      if (this.pending) {
        this.pending = false;
        this.validateCart();
      }
    }
  }

  async fetchCart() {
    const response = await fetch("/cart.js");
    if (!response.ok) throw new Error("Failed to fetch cart");
    return response.json();
  }

  async countItemsByTag(cart) {
    let foodCount = 0;
    let packagingCount = 0;

    for (const item of cart.items) {
      try {
        const product = await this.fetchProductData(item.handle);
        // Exact tags on purpose: San Jose products are tagged "food sj" /
        // "packaging sj" and SJ stores may mix both in one order (Minh
        // 2026-10-11), so only the OC/LA tags "food" and "packaging" count here.
        const tags = (product.tags || []).map((t) => String(t).toLowerCase());
        if (tags.includes("food")) foodCount++;
        if (tags.includes("packaging")) packagingCount++;
      } catch (error) {
        console.error(`Error fetching product ${item.handle}:`, error);
      }
    }

    return { foodCount, packagingCount };
  }

  async fetchProductData(handle) {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error(`Failed to fetch product: ${handle}`);
    return response.json();
  }

  isCartValid(foodCount, packagingCount) {
    return (
      (foodCount > 0 && packagingCount === 0) ||
      (foodCount === 0 && packagingCount > 0) ||
      (foodCount === 0 && packagingCount === 0)
    );
  }

  updateCheckoutState(isValid) {
    // Only touch the DOM when something actually changes, so the
    // MutationObserver in observeCartChanges() doesn't re-trigger itself.
    const checkoutButton = document.querySelector(
      '.mini-cart__button-container a[href="/cart"]'
    );
    let miniCartErrorMessage = document.getElementById(
      "mini-cart-error-message"
    );

    if (!isValid && checkoutButton) {
      if (checkoutButton.style.opacity !== "0.5") checkoutButton.style.opacity = "0.5";
      if (checkoutButton.style.pointerEvents !== "none") checkoutButton.style.pointerEvents = "none";

      if (!miniCartErrorMessage) {
        const recapContainer = document.querySelector(".mini-cart__recap");
        if (recapContainer) {
          miniCartErrorMessage = document.createElement("div");
          miniCartErrorMessage.id = "mini-cart-error-message";
          miniCartErrorMessage.style.cssText = `
          color: #D70000;
          margin: 12px 0;
          padding: 8px 12px;
          font-size: 13px;
          line-height: 1.5;
          text-align: center;
          background-color: #FFF0F0;
          border-radius: 4px;
          font-family: var(--text-font-family);
        `;
          miniCartErrorMessage.innerHTML =
            '<strong style="color: #D70000;">Order Guidelines:</strong> You can only add either "Packaging" items or "Food" items to a single Order Form. Please use separate forms for each type. Thank you!';
          recapContainer.insertBefore(miniCartErrorMessage, recapContainer.firstChild);
        }
      } else if (miniCartErrorMessage.style.display !== "block") {
        miniCartErrorMessage.style.display = "block";
      }
    } else if (checkoutButton) {
      if (checkoutButton.style.opacity !== "1") checkoutButton.style.opacity = "1";
      if (checkoutButton.style.pointerEvents !== "auto") checkoutButton.style.pointerEvents = "auto";

      if (miniCartErrorMessage && miniCartErrorMessage.style.display !== "none") {
        miniCartErrorMessage.style.display = "none";
      }
    }
  }
}

// Fresh check of the live cart, used by the checkout gate in
// snippets/custom-booked-dates.liquid so /cart checkout follows the same rule
// (OC/LA: one type per Shopify order; SJ may mix; Minh 2026-10-11). Resolves true when the cart is
// allowed; fails open on errors, like the booked-dates check.
CartValidator.prototype.checkNow = async function () {
  try {
    const cart = await this.fetchCart();
    const { foodCount, packagingCount } = await this.countItemsByTag(cart);
    this.lastCounts = { foodCount, packagingCount };
    return this.isCartValid(foodCount, packagingCount);
  } catch (error) {
    console.warn("[cart-type] check failed, allowing", error);
    return true;
  }
};

// Initialize the validator when the DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  window.cartTypeGuard = new CartValidator();
});

/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */
// const inputs = document.querySelectorAll('input[name="quantity"]');
// inputs.forEach(input => {
//   input.addEventListener('click', () => {
//     input.select();
//   });
// });
//   const myProduct = JSON.parse('{{ product_json | escape }}');
//   console.log(myProduct);

// // const inquireBtn = document.queryselector('.inquire-button');
// const section1 = document.getElementById('shopify-section-template--18145272004884__558bbb10-b1c6-4bd7-99c6-035ccd132dd9')

// const inquireBtn = document.querySelector(".inquire-button");

// inquireBtn.addEventListener("click", function () {
//   section1.scrollIntoView({
//     behavior: "smooth",
//   });
// });

// window.addEventListener('scroll', function () {
//   var scroll = window.pageYOffset || document.documentElement.scrollTop;
//   const backdrop = document.querySelectorAll('.header--inline, .nav-bar');
//   for (var i = 0; i < backdrop.length; i++)
//     if (scroll >= 300) {
//       backdrop[i].classList.add('headerBackdrop');
//     } else {
//       backdrop[i].classList.remove('headerBackdrop');
//     }
// });

// <script>(function() { var script = document.createElement('script'); script.src = "https://paperform.co/__embed.min.js";document.body.appendChild(script); })()</script>

/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 */
